import { motion } from "framer-motion";
import { MediaItem } from "./types/dailyTaskRwo";
import { Download, Trash, Video } from "lucide-react";
import { VIEW_MEDIA_URL } from "src/redux/api/appConst";
import { Badge } from "src/components/ui/Badge";
import { useState } from "react";
import {
  useApproveRwoMedia,
  useDeleteDwoMedia,
  useDeleteRwoMedia,
  useDwoMediaStatus,
  useRejectRwoMedia,
} from "./hooks/useMedia";
import { toast } from "react-toastify";

const STATUS_STYLES = {
  PENDING: "bg-amber-100  text-amber-600",
  APPROVED: "bg-emerald-100 text-emerald-600",
  REJECTED: "bg-red-100    text-red-500",
};

const TYPE_STYLES = {
  image: {
    bg: "bg-blue-50 group-hover:bg-blue-100",
  },
  video: {
    bg: "bg-cyan-50 group-hover:bg-cyan-100",
  },
  document: {
    bg: "bg-fuchsia-50 group-hover:bg-fuchsia-100",
  },
};

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogg)$/i;

const MediaCard = ({
  item,
  onView,
  onDownload,
  attendanceId,
  role,
}: {
  item: MediaItem;
  onView: () => void;
  onDownload: () => void;
  attendanceId: string;
  role: string;
}) => {

  const isVideo = (item: MediaItem) => {
    if (item.type === "video") return true;
    if (item.type === "image") return false;
    // type === "document": decide by extension
    return VIDEO_EXT.test(item.key);
  };

  const renderThumbnail = () => {
    if (!isVideo(item)) {
      return (
        <img
          src={`${VIEW_MEDIA_URL}/${item.key}`}
          alt={item.type}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      );
    }

    return (
      <>
        <video
          src={`${VIEW_MEDIA_URL}/${item.key}`}
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
          <div className="bg-white/90 rounded-full p-2 shadow-sm">
            <Video className="size-5 text-gray-700" />
          </div>
        </div>
      </>
    );
  };

  const status = item.status;

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const { mutateAsync: changeDwoStatus } = useDwoMediaStatus(attendanceId);
  const { mutate: approveMedia } = useApproveRwoMedia(attendanceId);
  const { mutate: rejectMedia } = useRejectRwoMedia(attendanceId);
  
  const { mutate: deleteDwoMedia } = useDeleteDwoMedia(attendanceId);
  const { mutate: deleteRwoMedia } = useDeleteRwoMedia(attendanceId);

  const handleDelete = (id: string) => {
    setDeletingId(id);

    if (role === "dwo") {
      deleteDwoMedia(id, { onSettled: () => setDeletingId(null) });
    }
    if (role === "rwo") {
      deleteRwoMedia(id, { onSettled: () => setDeletingId(null) });
    }
  };

  const onReject = (id: string, status:"REJECTED") => {

    setUpdatingId(id);

    if (role === "dwo") {
      changeDwoStatus(
        { mediaId: id, status },
        {
          onSuccess: () => toast.success(`Media ${status}.`),
          onSettled: () => setUpdatingId(null),
        },
      );
    }
    if( role === "rwo") {
      rejectMedia(
        { mediaId: id },
        {
          onSuccess: () => toast.success("Media Rejected!"),
          onSettled: () => setUpdatingId(null),
        },
      );
    }
  };

  const onAccept = (id: string, status:"APPROVED") => {
    
    setUpdatingId(id);

    if (role === "dwo") {
      changeDwoStatus(
        { mediaId: id, status },
        {
          onSuccess: () => toast.success(`Media ${status}.`),
          onSettled: () => setUpdatingId(null),
        },
      );
    }

    if( role === "rwo") {
      approveMedia({mediaId: id}, {
        onSuccess: () => toast.success("Media Approved!"),
        onSettled: () => setUpdatingId(null),
      });
    }
  };

  const updating = updatingId === item.id;
  const deleting = deletingId === item.id;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="group w-44 h-100 relative bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all"
    >
      <button
        onClick={onView}
        className="relative w-full aspect-square bg-gray-50 flex items-center justify-center overflow-hidden"
      >
        {renderThumbnail()}
      </button>

      <div className={`p-3 space-y-3 ${TYPE_STYLES[item.type].bg}`}>
        <div>
          <p className="text-xs font-semibold text-gray-700 truncate">
            {item.place}
          </p>
          <p className="text-xs text-gray-400 truncate mt-0.5">{item.work}</p>
        </div>

        {/* Accept / Reject */}
        <div className="flex gap-1.5">
          {(status === "PENDING" || status === "REJECTED") && (
            <button
              onClick={ () => onAccept(item.id, "APPROVED")}
              disabled={ updating }
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 text-xs font-semibold transition-all disabled:opacity-40"
            >
              { updating ? (
                <span className="loading loading-ring loading-sm" />
              ) : (
                <>
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Accept
                </>
              )}
            </button>
          )}

          {(status === "PENDING" || status === "APPROVED") && (
            <button
              onClick={() => onReject(item.id, "REJECTED")}
              disabled={ updating }
              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 text-xs font-semibold transition-all disabled:opacity-40"
            >
              { updating ? (
                <span className="loading loading-ring loading-sm" />
              ) : (
                <>
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Reject
                </>
              )}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 justify-between w-full">
          <button
            onClick={onDownload}
            title="Download"
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-all"
          >
            <Download className="size-4" />
          </button>

          <Badge className={`text-xs ${STATUS_STYLES[item.status]}`}>
            {item.status}
          </Badge>

          <button
            onClick={() => handleDelete(item.id)}
            disabled={ deleting }
            title="Delete"
            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all disabled:opacity-40"
          >
            {deleting ? (
              <span className="loading loading-ring loading-md" />
            ) : (
              <Trash className="size-4" />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default MediaCard;
